/**
 * Patient list view, +New-Plan typeahead modal, patient selection,
 * and the toast helper.
 *
 * Domain scope: everything that takes the user from "what should I look
 * at?" to "I'm now in this patient's detail view." The typeahead modal
 * is patient-list-adjacent (invoked from "+ New Plan") so it lives here
 * rather than in a separate module.
 *
 * `selectPatient` reaches back into main.ts for `loadPatientPlan` and
 * `renderClientsView`. ES module circular imports resolve fine because
 * those references are used inside function bodies — bindings are
 * hoisted so the live values resolve at call time, not import time.
 *
 * Extracted from main.ts as part of OMON-060 Phase 6b.2.
 */

import { state } from "../state";
import type { DetailActiveTab } from "../state";
import { clientsView } from "../dom";
import { escapeHtml } from "../utils/html";
import type { PatientInfo } from "../types";
import { loadPatientPlan } from "../main";
import { renderClientsView } from "./view";
import { go } from "../router";
import { sweetTreeMarkSvg } from "../sweettree_loader";
import { debounce } from "../utils/debounce";
import { loadOrgIndex, serviceOfTeam, type OrgNode } from "../loaders/services";
import { listFilterOptions, passesListFilters, pruneClient, type ListFilterChoice } from "./list-filters";

/** Service → Client filters (Tali, 2026-10-14). For the page's life. */
const listFilters: ListFilterChoice = { service: "", client: "" };
/** The organisation tree, once loaded; services fill in when it lands. */
let orgs: Map<string, OrgNode> | null = null;

/** Give every listed client its service. Rows from a background re-read
 *  arrive without one, so this runs on every repaint once the tree is in. */
function fillServices(): void {
  if (!orgs) return;
  for (const p of state.patients) {
    if (p.service === undefined) p.service = serviceOfTeam(p.teamOrgId, orgs);
  }
}

// ======================================================================
// Patient List
// ======================================================================

// OMON-054: split into "render the toolbar + list-body shell once" and
// "update only the list body". Previously the search-input handler called
// renderClientsView() which wholesale-replaced clientsView.innerHTML on
// every keystroke — destroying the input element and losing focus. Now the
// search input element is rendered once, and the input handler only mutates
// the list-body container's innerHTML. The browser keeps focus + caret
// position on the input naturally because the element itself isn't touched.
export function renderPatientList() {
  // OMON-034 Part A: search-toolbar with search input + top-level "+ New Plan" button.
  // The toolbar + empty list-body shell render once; the body re-renders on input.
  clientsView.innerHTML = `
    <div class="search-toolbar">
      <input type="text" class="search-bar" id="__patientSearch" placeholder="Search clients...">
      <button class="btn-primary" id="__topNewPlanBtn">+ New Plan</button>
    </div>
    <div class="list-filters">
      <label><span>Service</span><select id="__planFilterService" aria-label="Filter by service"></select></label>
      <label><span>Client</span><select id="__planFilterClient" aria-label="Filter by client"></select></label>
    </div>
    <div id="__patientListBody"></div>
  `;

  // Service → Client (Tali, 2026-10-14): a service narrows the clients; a
  // new service clears a client choice it doesn't contain.
  clientsView.querySelector<HTMLSelectElement>("#__planFilterService")?.addEventListener("change", (e) => {
    listFilters.service = (e.currentTarget as HTMLSelectElement).value;
    Object.assign(listFilters, pruneClient(state.patients, listFilters));
    updatePatientListBody();
  });
  clientsView.querySelector<HTMLSelectElement>("#__planFilterClient")?.addEventListener("change", (e) => {
    listFilters.client = (e.currentTarget as HTMLSelectElement).value;
    updatePatientListBody();
  });
  if (!orgs) {
    loadOrgIndex()
      .then((m) => {
        orgs = m;
        updatePatientListBody();
      })
      .catch((err) => console.warn("Service filter: organisations did not load", err));
  }

  // Toolbar handlers — bound once; survive every keystroke since the input
  // element isn't recreated on input.
  //
  // OMON-351: debounced — each keystroke rebuilt all ~290 rows (and re-bound
  // their click handlers). A typed name now costs one rebuild per pause;
  // Enter shows the result at once. Same pattern as the Goals Inventory
  // search (main.ts). A fresh debouncer per render, so a keystroke pending
  // from a previous list can never fire into this one.
  const searchSoon = debounce(updatePatientListBody, 150);
  const search = clientsView.querySelector<HTMLInputElement>("#__patientSearch");
  search?.addEventListener("input", () => searchSoon());
  search?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") searchSoon.flush();
  });
  clientsView.querySelector("#__topNewPlanBtn")?.addEventListener("click", () => openNewPlanModal());

  // Initial body render with empty search term
  updatePatientListBody();
}

/**
 * OMON-351: repaint the rows from `state.patients` under the CURRENT search
 * term, leaving the toolbar (and the planner's half-typed search) alone. Used
 * when a background re-read of the list lands. No-op if the list isn't shown.
 */
export function refreshPatientListBody(): void {
  updatePatientListBody();
}

/** Refill both dropdowns from the rows in hand, keeping the choices. Until
 *  the organisation tree lands, Service reads "Loading services…". */
function paintListFilters(): void {
  const svc = clientsView.querySelector<HTMLSelectElement>("#__planFilterService");
  const cli = clientsView.querySelector<HTMLSelectElement>("#__planFilterClient");
  if (!svc || !cli) return;
  const opt = (key: string, label: string, cur: string) =>
    `<option value="${escapeHtml(key)}"${key === cur ? " selected" : ""}>${escapeHtml(label)}</option>`;
  svc.innerHTML = orgs
    ? opt("", "All services", listFilters.service) +
      listFilterOptions(state.patients, "service", listFilters).map((o) => opt(o.key, `${o.label} (${o.count})`, listFilters.service)).join("")
    : opt("", "Loading services…", "");
  svc.disabled = !orgs;
  cli.innerHTML = opt("", "All clients", listFilters.client) +
    listFilterOptions(state.patients, "client", listFilters).map((o) => opt(o.key, o.label, listFilters.client)).join("");
}

function updatePatientListBody() {
  const searchEl = clientsView.querySelector<HTMLInputElement>("#__patientSearch");
  const searchTerm = (searchEl?.value || "").toLowerCase();
  const body = clientsView.querySelector<HTMLElement>("#__patientListBody");
  if (!body) return;

  fillServices();
  paintListFilters();
  const filtered = state.patients.filter(
    (p) => p.name.toLowerCase().includes(searchTerm) && passesListFilters(p, listFilters),
  );

  let html: string;
  if (filtered.length === 0) {
    // OMON-034 Part A item 3: "Add a referral" empty-state affordance — only when
    // the search query is non-empty and produces zero matches.
    const referralLink = searchTerm
      ? `<a class="referral-link" id="__addReferralLink">Not in the list? Add as a new referral →</a>`
      : "";
    html = `<div class="empty-state">
      <h3>${state.patients.length === 0 ? "No clients found" : "No matches"}</h3>
      <p>${state.patients.length === 0 ? "No active episodes of care in the system." : "Try a different search term."}</p>
      ${referralLink}
    </div>`;
  } else {
    html = filtered.map((p) => {
      // OMON-034 Part A item 4: status-aware row classes/badges.
      const rowClass = p.status === "pending" ? "row-pending" : p.status === "onhold" ? "row-onhold" : "";
      const statusBadge = p.status === "pending"
        ? '<span class="badge badge-pending">no plan started</span>'
        : p.status === "onhold"
          ? '<span class="badge badge-onhold">on hold</span>'
          : `<span class="badge badge-active">${escapeHtml(p.status)}</span>`;
      return `
      <div class="card patient-row ${rowClass}" data-patient-id="${p.docId}">
        <div>
          <div class="patient-name">${escapeHtml(p.name)}</div>
          <div class="patient-meta">EoC: ${p.docId.slice(0, 8)}...</div>
        </div>
        <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;justify-content:flex-end;">
          ${p.hasPlan ? '<span class="badge badge-plan">SOMA plan</span>' : ""}
          ${p.hasPlan ? `<span class="badge badge-version">v${p.planVersion || 1}</span>` : ""}
          ${p.hasDraft ? `<span class="badge badge-draft">v${(p.planVersion || 0) + 1} · draft</span>` : ""}
          ${statusBadge}
        </div>
      </div>
    `;
    }).join("");
  }

  body.innerHTML = html;

  // Re-bind handlers that live inside the body (they are recreated each call).
  body.querySelector("#__addReferralLink")?.addEventListener("click", () => openNewPlanModal(searchTerm));
  body.querySelectorAll(".patient-row").forEach((row) => {
    row.addEventListener("click", () => {
      // OMON-202: navigate via the URL so the back button can return here.
      const id = (row as HTMLElement).dataset.patientId!;
      go({ kind: "client", eocId: id, tab: "developmental" });
    });
  });
}

// ======================================================================
// OMON-034 Part B: New-Plan modal (Path B identity step + typeahead)
// ======================================================================

let typeaheadDebounceTimer: number | null = null;

function openNewPlanModal(prefillTerm = "") {
  const modal = document.createElement("div");
  modal.className = "modal-overlay";
  modal.id = "__newPlanModal";
  modal.innerHTML = `
    <div class="modal-box new-plan-modal">
      <div class="modal-header">
        <h3>+ New Plan</h3>
        <button class="modal-close" id="__newPlanClose" aria-label="Close">&times;</button>
      </div>
      <input type="text" class="search-bar typeahead-input" id="__typeaheadInput"
        placeholder="Last name..." value="${escapeHtml(prefillTerm)}" autocomplete="off">
      <div class="typeahead-results" id="__typeaheadResults"></div>
    </div>
  `;
  document.body.appendChild(modal);

  const input = modal.querySelector<HTMLInputElement>("#__typeaheadInput")!;
  modal.querySelector<HTMLButtonElement>("#__newPlanClose")!
    .addEventListener("click", closeNewPlanModal);
  modal.addEventListener("click", (e) => { if (e.target === modal) closeNewPlanModal(); });
  document.addEventListener("keydown", escClose);
  input.addEventListener("input", onTypeaheadInput);

  input.focus();
  if (prefillTerm) renderTypeaheadResults(prefillTerm);
}

function closeNewPlanModal() {
  document.getElementById("__newPlanModal")?.remove();
  document.removeEventListener("keydown", escClose);
  if (typeaheadDebounceTimer != null) {
    clearTimeout(typeaheadDebounceTimer);
    typeaheadDebounceTimer = null;
  }
}

function escClose(e: KeyboardEvent) {
  if (e.key === "Escape") closeNewPlanModal();
}

function onTypeaheadInput(e: Event) {
  const term = (e.target as HTMLInputElement).value;
  if (typeaheadDebounceTimer != null) clearTimeout(typeaheadDebounceTimer);
  typeaheadDebounceTimer = window.setTimeout(() => renderTypeaheadResults(term), 200);
}

function renderTypeaheadResults(term: string) {
  const resultsEl = document.getElementById("__typeaheadResults");
  if (!resultsEl) return;
  if (!term.trim()) {
    resultsEl.innerHTML = "";
    return;
  }
  // OMON-034 Q4(substring), Q5(show all): in-memory filter against the state.patients
  // list (pre-ONE-004 = the small explicit-EoC set; post-ONE-004 it scales to
  // ~683 auto-materialised entries naturally).
  const t = term.toLowerCase();
  const matches = state.patients.filter((p) => p.name.toLowerCase().includes(t));
  if (matches.length === 0) {
    // OMON-034 Q8(b): "this is a new person" affordance only when 0 matches.
    // OMON-034 Q10(a): stub inline-message for the new-person path (pre-ONE-004).
    resultsEl.innerHTML = `
      <div class="typeahead-empty">
        No match for "${escapeHtml(term)}".<br>
        <a id="__newPersonLink">+ This is a new person, not in the list</a>
      </div>
      <div class="typeahead-stub-message" id="__newPersonStub" style="display:none;">
        Creating new client records ships with ONE-004 backfill — preview disabled.
      </div>
    `;
    document.getElementById("__newPersonLink")?.addEventListener("click", () => {
      const stub = document.getElementById("__newPersonStub");
      if (stub) stub.style.display = "block";
    });
    return;
  }
  // OMON-034 Q6(show all decoration): name + plan-status.
  resultsEl.innerHTML = matches.map((p) => {
    const decoration = p.hasPlan
      ? '<span class="decoration">active plan</span>'
      : p.status === "pending"
        ? '<span class="decoration">no plan started</span>'
        : p.status === "onhold"
          ? '<span class="decoration">on hold</span>'
          : `<span class="decoration">${escapeHtml(p.status)}</span>`;
    return `
      <div class="typeahead-row" data-patient-id="${p.docId}">
        <span class="name">${escapeHtml(p.name)}</span>
        ${decoration}
      </div>
    `;
  }).join("");
  resultsEl.querySelectorAll(".typeahead-row").forEach((row) => {
    row.addEventListener("click", () => {
      const id = (row as HTMLElement).dataset.patientId!;
      const patient = state.patients.find((p) => p.docId === id);
      if (patient) selectFromTypeahead(patient);
    });
  });
}

function selectFromTypeahead(patient: PatientInfo) {
  closeNewPlanModal();
  // OMON-034 Q7(toast): notice when Path-B lands on an existing active plan.
  if (patient.hasPlan) {
    showToast(`Opening existing SOMA plan for ${patient.name} for editing.`);
  } else if (patient.status === "onhold") {
    showToast(`${patient.name} has an on-hold plan — review status before continuing.`);
  }
  // OMON-202: route so the URL reflects the opened client and Back works.
  go({ kind: "client", eocId: patient.docId, tab: "developmental" });
}

// ======================================================================
// Toast (helper used by typeahead + plan-publish flows)
// ======================================================================

export function showToast(message: string, durationMs = 4000) {
  let toast = document.getElementById("__toast") as HTMLDivElement | null;
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "__toast";
    toast.className = "toast";
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  // Force reflow so the transition fires reliably even on rapid re-trigger.
  void toast.offsetWidth;
  toast.classList.add("visible");
  setTimeout(() => {
    toast?.classList.remove("visible");
  }, durationMs);
}

// ======================================================================
// Patient selection — list row → detail view
// ======================================================================

export async function selectPatient(patient: PatientInfo, tab: DetailActiveTab = "developmental") {
  state.selectedPatient = patient;
  state.builderMode = "view";
  state.builderGoals = [];
  // OMON-121: reset detail-view active tab on patient change so each patient is
  // examined starting from the developmental goals view — unless the router is
  // restoring a deep link to a specific tab (OMON-202).
  state.detailActiveTab = tab;
  // Ed 2026-09-08 (the CoS switcher-spinner's sibling): the plan load can
  // take seconds, and bare text reads as a hang. The SweetTree loading mark
  // reads as PROGRESS rather than waiting; the artwork lives in ONE module
  // (sweettree_loader.ts — see its copy-on-purpose note).
  clientsView.innerHTML = `<div class="empty-state tree-loader">
    ${sweetTreeMarkSvg("tree-loader-mark")}
    <h3>Loading ${escapeHtml(patient.name)}'s plan…</h3>
  </div>`;

  try {
    await loadPatientPlan(patient);
    renderClientsView();
  } catch (err) {
    console.error("Failed to load plan:", err);
    clientsView.innerHTML = `<div class="empty-state"><h3>Error loading SOMA plan</h3><p>Check console for details.</p></div>`;
  }
}
